if (!customElements.get('hero-slider')) {
  class HeroSlider extends SwiperComponent {
    constructor() {
      super();
      this.lastActiveIndex = null;
      this.videoAdvanceCleanup = null;
    }

    connectedCallback() {
      // Override parent's connectedCallback
      // We'll use SwiperComponent's init method
      this.swiperOptions = this.buildHeroSliderOptions();
      this.swiperInitialized = false;
      this.section = this.closest('section');
      this.observer = null;

      // Initialize swiper (using SwiperComponent's logic)
      if (this.section && this.section.classList.contains('shopify-section')) {
        this.observer = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
              observer.disconnect();
            }
          });
        });
        this.observer.observe(this.section);
      }

      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', () => this.init());
        window.addEventListener('shopify:section:select', () => this.init());

        setTimeout(() => {
          if (!this.swiperInitialized) this.init();
        }, 100);
      } else {
        window.addEventListener('load', () => this.init());
      }

      // Hero-specific event listeners
      window.addEventListener('shopify:block:select', e => {
        if (this.swiper && e.target.closest('hero-slider') === this) {
          const selectedSlideIndex = +e.target.dataset.index;
          this.swiper.slideTo(selectedSlideIndex, 600);
        }
      });

      this.addKeyboardNavigation();
    }

    buildHeroSliderOptions() {
      const autoplayOptions = {
        delay: Number(this.dataset.autoplayInterval) || 5000
      };

      return {
        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: false,
        autoHeight: this.dataset.autoHeight === 'true',
        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          renderBullet: function (i, className) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>
                <svg class="square-progress" width="26" height="26">
                  <rect class="square-origin" width="26" height="26" rx="5" ry="5" />
                </svg>
                <svg class="progress" width="1.8rem" height="1.8rem" style="inset-inline-start: ${0 - (i * 2.4 + 3.4)}rem">
                  <circle class="circle-origin" r="0.8rem" cx="0.95rem" cy="0.95rem"></circle>
                </svg>
              </button>
            `;
          }
        },
        autoplay: this.dataset.autoplay === 'true' ? autoplayOptions : false,
        on: {
          init: this.handleSlideChange.bind(this),
          slideChange: this.handleSlideChange.bind(this)
        }
      };
    }

    init() {
      if (this.swiperInitialized) return;
      this.swiperInitialized = true;

      // Use custom Swiper options for HeroSlider
      this.swiper = new Swiper(this, this.swiperOptions);

      // Maintain slider property for backward compatibility
      this.slider = this.swiper;
      this.syncSlideVideos(this.swiper);
      this.syncSlideAutoplay(this.swiper);
      this.bindViewportChange();
    }

    handleSlideChange(swiper) {
      this.handleSlideChangeAnimations(swiper);
      this.syncSlideVideos(swiper);
      this.syncSlideAutoplay(swiper);

      /** change header menu color for active slide */
      let headerInner = document.querySelector('.header__inner');
      let heroInners = document.querySelectorAll('.hero__inner');

      if (!headerInner || !heroInners) {
        return;
      }

      // change --transparent-header-menu-text-color value on document style attributes
      document.documentElement.style.setProperty(
        '--transparent-header-menu-text-color',
        heroInners[swiper.activeIndex].dataset.headerMenuTextColor
      );
    }
    /**
     * get direction & set custom transitions
     * with delay for content animation items
     * */
    handleSlideChangeAnimations(swiper) {
      let delay = 300;
      const queryElement = `[data-index="${swiper.activeIndex}"]`;
      const queryArray = [...(swiper.wrapperEl.querySelector(queryElement).querySelectorAll('.hero__animation') || [])];
      if (!queryArray.length) {
        return;
      }
      const allowMobileAnimation = swiper.wrapperEl.closest('.hero__content')?.dataset.animationMobile === 'true';
      if (!allowMobileAnimation && DeviceDetector.isMobile()) {
        queryArray.forEach(element => {
          element.classList.remove('in-delay');
        });
        return;
      }
      // set all element in delay
      queryArray.forEach(element => {
        element.classList.add('in-delay');
      });

      // get direction and set transitions
      if (this.lastActiveIndex > swiper.activeIndex) {
        delay = 650 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      } else if (this.lastActiveIndex < swiper.activeIndex) {
        delay = 1000 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      }

      // set delay calculated per item
      setTimeout(() => {
        if (this.lastActiveIndex > swiper.activeIndex) {
          delay = 325 + delay;
        } else if (this.lastActiveIndex < swiper.activeIndex) {
          delay = 500 + delay;
        }
        queryArray.forEach((element, index) => {
          if (index === 1) {
            delay = delay + 50;
          } else if (index === 2) {
            delay = delay + 175;
          } else if (index === 3) {
            delay = delay + 300;
          }
          element.classList.remove('in-delay');
        });
      }, delay);

      // set last active global
      this.lastActiveIndex = swiper.activeIndex;
    }

    syncSlideAutoplay(swiper) {
      const slide = swiper.slides[swiper.activeIndex];

      if (!slide) return;

      this.unbindVideoAdvance();
      this.updateProgressTime(Number(slide.dataset.progressMs) || 0);

      if (this.dataset.autoplay !== 'true' || !swiper.autoplay) return;

      if (slide.dataset.advanceOnVideoEnd === 'true') {
        swiper.autoplay.stop();
        this.bindVideoAdvance(slide);
        return;
      }

      if (!slide.hasAttribute('data-slide-autoplay')) return;

      const delay = Number(slide.dataset.slideAutoplay);

      if (delay <= 0) {
        swiper.autoplay.stop();
        return;
      }

      if (swiper.params.autoplay) {
        swiper.params.autoplay.delay = delay;
      }

      if (swiper.autoplay.running === false) {
        swiper.autoplay.start();
      }
    }

    updateProgressTime(ms) {
      const root = this.closest('.shopify-section') || this;

      root.style.setProperty('--slideshow-pagination-progress-time', `${Math.max(ms, 0)}ms`);
      this.classList.toggle('hero-slider--no-progress', !ms || ms <= 0);
    }

    bindViewportChange() {
      if (this.viewportMediaQuery) return;

      this.viewportMediaQuery = window.matchMedia('(max-width: 749px)');
      this.onViewportChange = () => {
        if (!this.swiper) return;

        this.syncSlideVideos(this.swiper);
      };

      this.viewportMediaQuery.addEventListener('change', this.onViewportChange);
    }

    syncSlideVideos(swiper) {
      if (!swiper?.slides) return;

      const activeSlide = swiper.slides[swiper.activeIndex];

      for (const slide of swiper.slides) {
        const videos = slide.querySelectorAll('video');
        const visibleVideo = this.getVisibleMedia(slide, 'video');
        const isActive = slide === activeSlide;

        for (const video of videos) {
          if (isActive && video === visibleVideo) {
            video.muted = true;
            video.playsInline = true;

            const playPromise = video.play();

            if (playPromise) playPromise.catch(() => {});
          } else {
            video.pause();
          }
        }
      }
    }

    getVisibleMedia(slide, selector) {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const elements = [...slide.querySelectorAll(selector)];

      if (!elements.length) return null;

      const match = elements.find(element => {
        const media = element.closest('.hero__media');

        if (!media) return false;

        if (isMobile) return media.classList.contains('small-up-hide');

        return !media.classList.contains('small-up-hide');
      });

      return match || elements[0];
    }

    bindVideoAdvance(slide) {
      const video = this.getVisibleMedia(slide, 'video');

      if (video) {
        this.bindHtmlVideoAdvance(video);
        return;
      }

      const iframe = this.getVisibleMedia(slide, 'iframe');

      if (iframe) this.bindExternalVideoAdvance(iframe);
    }

    bindHtmlVideoAdvance(video) {
      let advanced = false;

      const advance = () => {
        if (advanced) return;

        advanced = true;
        this.goToNextSlide();
      };

      const onEnded = () => advance();

      const startPlayback = () => {
        video.loop = false;
        video.currentTime = 0;

        const playPromise = video.play();

        if (playPromise) playPromise.catch(() => {});

        if (Number.isFinite(video.duration)) {
          this.updateProgressTime(video.duration * 1000);
        }
      };

      video.addEventListener('ended', onEnded);

      if (video.readyState >= 1) {
        startPlayback();
      } else {
        video.addEventListener('loadedmetadata', startPlayback, { once: true });
      }

      this.videoAdvanceCleanup = () => {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('loadedmetadata', startPlayback);
        video.pause();
        video.currentTime = 0;
      };
    }

    bindExternalVideoAdvance(iframe) {
      let advanced = false;

      const advance = () => {
        if (advanced) return;

        advanced = true;
        this.goToNextSlide();
      };

      const onMessage = event => {
        const youtubeOrigin = event.origin === 'https://www.youtube.com';
        const vimeoOrigin = event.origin === 'https://player.vimeo.com';

        if (!youtubeOrigin && !vimeoOrigin) return;

        let data = event.data;

        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (_error) {
            return;
          }
        }

        if (!data || typeof data !== 'object') return;

        const youtubeEnded = data.event === 'onStateChange' && data.info === 0;
        const vimeoEnded = data.event === 'finish' || data.event === 'ended';

        if (youtubeEnded || vimeoEnded) advance();
      };

      window.addEventListener('message', onMessage);

      iframe.contentWindow?.postMessage(JSON.stringify({ event: 'listening' }), '*');
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'addEventListener', value: 'finish' }),
        '*'
      );
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'addEventListener', value: 'ended' }),
        '*'
      );

      this.videoAdvanceCleanup = () => {
        window.removeEventListener('message', onMessage);
      };
    }

    unbindVideoAdvance() {
      if (typeof this.videoAdvanceCleanup !== 'function') return;

      this.videoAdvanceCleanup();
      this.videoAdvanceCleanup = null;
    }

    goToNextSlide() {
      const swiper = this.swiper || this.slider;

      if (!swiper) return;

      swiper.slideNext();
    }

    disconnectedCallback() {
      this.unbindVideoAdvance();

      if (this.viewportMediaQuery && this.onViewportChange) {
        this.viewportMediaQuery.removeEventListener('change', this.onViewportChange);
      }
    }

    addKeyboardNavigation() {
      document.addEventListener('keydown', event => {
        if (this.isInViewport()) {
          if (event.key === 'ArrowRight') {
            const nextButton = this.querySelector('.swiper-button--next');
            if (nextButton) {
              nextButton.click();
            }
          }
          if (event.key === 'ArrowLeft') {
            const prevButton = this.querySelector('.swiper-button--prev');
            if (prevButton) {
              prevButton.click();
            }
          }
        }
      });
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top < window.innerHeight && // top part in viewport
        rect.bottom > 0 && // bottom part in viewport
        rect.left < window.innerWidth && // left part in viewport
        rect.right > 0 // right part in viewport
      );
    }
  }
  customElements.define('hero-slider', HeroSlider);
}

const syncStandaloneHeroVideos = () => {
  const banners = document.querySelectorAll('.hero-banner-new');
  const isMobile = window.matchMedia('(max-width: 749px)').matches;

  for (const banner of banners) {
    if (banner.querySelector('hero-slider')) continue;

    const videos = banner.querySelectorAll('video');

    for (const video of videos) {
      const media = video.closest('.hero__media');

      if (!media) continue;

      const isMobileMedia = media.classList.contains('small-up-hide');
      const isDesktopOnly = media.classList.contains('small-hide');
      const visible = isMobile ? isMobileMedia || !isDesktopOnly : !isMobileMedia;

      if (visible) {
        video.muted = true;
        video.playsInline = true;

        const playPromise = video.play();

        if (playPromise) playPromise.catch(() => {});
      } else {
        video.pause();
      }
    }
  }
};

document.addEventListener('DOMContentLoaded', syncStandaloneHeroVideos);
window.matchMedia('(max-width: 749px)').addEventListener('change', syncStandaloneHeroVideos);
